/**
 * 「站点必选」四页共用的**统一判定与文案**（2026-10-07 用户裁定）。
 *
 * 用户原话：「可以约束：**广告指标表、广告管理、货号看板、素材看板**，都只能选定一个站点才显示内容，
 * **不指定站点就没有内容**。」
 *
 * ## 为什么单独一个模块（而不是各页各写）
 * 已有两页（广告管理、花费总览）是**各写一段** `disabled={shopId === ""}`；再让三页各抄一遍，
 * 就会变成"四处文案/四处判定"⇒ 分叉（本会话在"两份清单/两套渲染"上栽过多次）。
 * ⇒ 判定与文案**只此一处**，页面只负责"kind !== ready 就不渲染内容"。
 *
 * ## ⚠️ 与既有 `resolveShopDefault` 的关系（**有意不同，须记住**）
 * `globalContext.resolveShopDefault` 的口径是「已存值无效 ⇒ **一律回落「全部店铺」**，
 * **绝不猜一个店**」。那是**其它页面**（站点筛选器）的默认值语义。
 * 本模块服务于**"必须选一个站点"的四页**，按用户新要求额外加一条：
 * **只可见一个店铺 ⇒ 自动选中它**（否则用户每次都要手选）。
 * ⇒ 两条规则**并存但作用域不同**：改这个模块不要顺手去改 `resolveShopDefault`（会让其它页悄悄改变口径）。
 */

/** 站点必选页面的判定结果。 */
export type ShopRequirement =
  /** 已经选定一个**有效**站点 ⇒ 正常渲染内容。 */
  | { readonly kind: "ready"; readonly shopId: string }
  /** 只可见一个店铺 ⇒ **自动选中它**（调用方写一次 store/URL，然后按 `shopId` 渲染）。 */
  | { readonly kind: "auto_select"; readonly shopId: string }
  /** 可见多个店铺，但用户没选 ⇒ **不显示内容**（提示去选站点）。 */
  | { readonly kind: "none_selected" }
  /** 一个可见店铺都没有 ⇒ **不显示内容**（提示没有可用站点）。 */
  | { readonly kind: "no_shop" };

/** 判定：把"当前选择 + 可见店铺"折叠成上面四种情况之一（纯函数，可单测）。 */
export function resolveShopRequirement(input: {
  readonly visibleShopIds: readonly string[];
  /** 当前选择（`""` = 未选，与 `ALL_SHOPS` 同义）。 */
  readonly shopId: string;
}): ShopRequirement {
  const visible = [...new Set(input.visibleShopIds)].sort();
  if (visible.length === 0) return { kind: "no_shop" };
  const current = input.shopId.trim();
  if (current !== "" && visible.includes(current)) return { kind: "ready", shopId: current };
  if (visible.length === 1) return { kind: "auto_select", shopId: visible[0] as string };
  return { kind: "none_selected" };
}

/**
 * ⭐ 「未选站点」与「该站点没有数据」**必须是两套文案**（需要不同动作的状态必须可区分）：
 * 前者要用户"去选站点"，后者是"这个站点确实没数据"（动作是换站点/换区间，不是"去选站点"）。
 */
export const SHOP_REQUIRED_NOTICES = {
  none_selected: {
    title: "请先选择站点",
    description: "本页按站点取数：在右上角的站点选择器里选定一个站点后才会显示内容。",
    actionLabel: "请选择站点",
  },
  no_shop: {
    title: "没有可用的站点",
    description: "你的账号当前没有可见的站点。请联系管理员分配店铺权限后重试。",
    actionLabel: "暂无可用站点",
  },
} as const;

/** 判定 → 文案（`ready`/`auto_select` 不需要文案 ⇒ 返回 `null`）。 */
export function shopRequiredNotice(
  requirement: ShopRequirement,
): { readonly title: string; readonly description: string; readonly actionLabel: string } | null {
  return requirement.kind === "ready" || requirement.kind === "auto_select"
    ? null
    : SHOP_REQUIRED_NOTICES[requirement.kind];
}

/** 是否应该"不显示内容"（`ready` 才有内容；`auto_select` 由调用方先写选择再渲染）。 */
export function shopRequiredBlocksContent(requirement: ShopRequirement): boolean {
  return requirement.kind === "none_selected" || requirement.kind === "no_shop";
}
